/* eslint-disable react/jsx-one-expression-per-line */
import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import { useQuery } from '@apollo/client';
import { Link, useParams } from 'react-router-dom';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { Container, styled } from '@mui/system';
import {
  Button, Grid, Typography, Stack, CircularProgress, useMediaQuery,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import JsxParser from 'react-jsx-parser';
import { useTheme } from '@emotion/react';
import { Form } from 'react-final-form';

import { getInviteFormByPathGql, getInviteFormGql, getInviteGql } from '../InviteResponse/gql';
import Summary from './components/Summary';
import RsvpHeading from '../../Components/RsvpHeading';
import { GeneralContext } from '../../util/Store';
import GlobalStyles from '../../Components/GlobalStyles';
import FormContainer from '../../Components/FormContainer';
import { ThemeContext } from '../../util/ThemeStore';
import ExternalLink from '../../Components/ExternalLink';
import DynamicSummary from '../InviteResponse/components/DynamicSummary';
import KioskExitButton from '../../Components/KioskExitButton';
import useIsKioskMode from '../../util/useIsKioskMode';
import useKioskIdleReset from '../../util/useKioskIdleReset';

const ErrorTypo = styled(Typography)({
  textAlign: 'center',
  marginTop: '35vh',
});

export default function Accepted() {
  const { inviteCode, path } = useParams();
  const snackbar = useSnackbar();
  const [form, setForm] = useState(null);
  const [overrideFormId, setOverrideFormId] = useState(null);
  const theme = useTheme();
  const generalStore = useContext(GeneralContext);
  const themeStore = useContext(ThemeContext);
  const matchesSm = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    data,
    loading,
  } = useQuery(getInviteGql, {
    fetchPolicy: 'no-cache',
    variables: {
      inviteCode,
    },
    onError: () => snackbar.showMessage('Fehler beim laden.'),
    onCompleted: ({ getInvite }) => {
      if (getInvite.customAttributes?.switchForm && getInvite.customAttributes?.switchForm.startsWith('formSwitch:')) {
        setOverrideFormId(getInvite.customAttributes.switchForm.replace('formSwitch:', ''));
      }
      generalStore.setFooterLinks(getInvite.inviteForm?.frontendSettings?.footerLinks);
      setForm(getInvite.inviteForm);
    },
  });

  const { loading: inviteFormLoading } = useQuery(getInviteFormGql, {
    skip: !overrideFormId,
    fetchPolicy: 'no-cache',
    variables: {
      formInviteId: overrideFormId,
    },
    onCompleted: ({ getInviteForm }) => {
      setForm(getInviteForm);
    },
  });

  const { data: inviteFormResult, loading: formLoading } = useQuery(getInviteFormByPathGql, {
    fetchPolicy: 'no-cache',
    variables: { path },
    onCompleted: (result) => {
      if (result?.getInviteFormByPath?.themeConfig?.typography) {
        themeStore.setTypography(result?.getInviteFormByPath?.themeConfig?.typography);
      }
    },
  });

  const invite = useMemo(() => data?.getInvite, [data?.getInvite]);
  const inviteForm = useMemo(() => inviteFormResult?.getInviteFormByPath?.inviteForm, [inviteFormResult?.getInviteFormByPath?.inviteForm]);
  const isKioskMode = useIsKioskMode();
  const kioskRedirectUrl = data?.getInvite?.inviteForm?.frontendSettings?.kioskRedirectUrl;
  const kioskIdle = useKioskIdleReset(path, kioskRedirectUrl);

  useEffect(() => {
    if (!data?.getInvite) return;
    generalStore.setContactInfo(data?.getInvite?.inviteForm?.frontendSettings?.contactInfo);
  }, [data?.getInvite]);

  if (!invite || loading || inviteFormLoading || formLoading) {
    return (
      <Stack alignItems="center" sx={{ mt: 5 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!inviteCode) return (<ErrorTypo variant="h2">Fehlerhafter Link</ErrorTypo>);
  return (
    <Container
      maxWidth="lg"
      sx={{
        paddingX: '8px',
        ...(isKioskMode ? {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: '100dvh',
        } : {}),
      }}
    >
      {isKioskMode && (
        <Dialog open={kioskIdle.warningOpen} disableEscapeKeyDown>
          <DialogTitle>Noch da?</DialogTitle>
          <DialogContent>
            <Typography>
              {`Diese Seite wird in ${kioskIdle.secondsLeft} Sekunden für den nächsten Gast zurückgesetzt.`}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={kioskIdle.resetNow} color="inherit">
              Jetzt zurücksetzen
            </Button>
            <Button onClick={kioskIdle.continueSession} variant="contained" autoFocus>
              Weiter
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <GlobalStyles
        colors={inviteFormResult?.getInviteFormByPath?.colors}
        customCss={inviteFormResult?.getInviteFormByPath?.customCss}
        font={inviteFormResult?.getInviteFormByPath?.font}
      />
      <RsvpHeading logoUrl={inviteFormResult?.getInviteFormByPath?.logoUrl} inviteForm={inviteForm} />
      <FormContainer sx={{ pb: 4, mb: 1 }} elevation={4} matchesSm={matchesSm}>
        <Grid container direction="row" justifyContent="center" sx={{ mb: 2 }}>
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 80 }} />
        </Grid>
        {data?.getInvite?.inviteForm?.frontendSettings?.acceptedHeadline ? (
          <Typography sx={{ textAlign: 'center', mb: 2 }} variant="h4">
            {data?.getInvite?.inviteForm?.frontendSettings?.acceptedHeadline}
          </Typography>
        ) : (
          <Typography sx={{ textAlign: 'center', mb: 2 }} variant="h4">
            Vielen Dank für Ihre Registrierung
          </Typography>
        )}
        {isKioskMode ? (
          <>
            <Typography sx={{ textAlign: 'center', mb: 2 }}>
              Ihr Namensschild können Sie am Help Desk vor Ort abholen.
            </Typography>
            <Grid container direction="row" justifyContent="center" sx={{ mb: 2 }}>
              <KioskExitButton redirectUrl={kioskRedirectUrl} />
            </Grid>
          </>
        ) : (
          <Grid container direction="row" justifyContent="center">
            <Form
              initialValues={invite}
              onSubmit={() => {}}
              render={() => (
                <JsxParser
                  components={{
                    Typography, Stack, Summary, Grid, Link: ExternalLink, DynamicSummary,
                  }}
                  jsx={form?.successDescription}
                  bindings={{
                    invite,
                    form,
                    inviteForm,
                  }}
                  renderInWrapper={false}
                  autoCloseVoidElements
                />
              )}
            />
          </Grid>
        )}
        {!data?.getInvite?.inviteForm?.frontendSettings?.readonlyAfterRegistration && (
          <Grid container direction="row" justifyContent="center" sx={{ pt: 4 }}>
            <Button
              variant="outlined"
              to={`/${path}/response/${inviteCode}?update=true`}
              component={Link}
            >
              Änderungen vornehmen
            </Button>
          </Grid>
        )}
      </FormContainer>
    </Container>
  );
}
